import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  limit,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { FirebaseError } from 'firebase/app';
import { db, functions } from '../../lib/firebase';
import { useAuthState } from '../../lib/partnerAuth';
import { useSEO } from '../../hooks/useSEO';
import { DashboardFilters } from '../../components/partners/DashboardFilters';
import { CampaignAnalyticsPanel } from '../../components/partners/CampaignAnalyticsPanel';
import {
  DEFAULT_PARTNER_FILTERS,
  computeFilteredStats,
  firestoreTimestampToDate,
  type FilteredPartnerStats,
  type PartnerFilterState,
  type ReferralClickRow,
  type ReferralInstallRow,
  type PurchaseRow,
} from '../../lib/partnerStatsFilters';
import styles from './Campaigns.module.css';

interface PartnerRow {
  id: string;
}

interface CampaignRow {
  shortCode: string;
  name: string;
  createdAt: Date | null;
}

interface CampaignWithStats extends CampaignRow {
  url: string;
  stats: FilteredPartnerStats;
}

const createPartnerCampaign = httpsCallable<
  { name: string; shortCode?: string },
  { shortCode: string; url: string }
>(functions, 'createPartnerCampaign');

const deletePartnerCampaign = httpsCallable<{ shortCode: string }, { ok: boolean }>(
  functions,
  'deletePartnerCampaign',
);

export function PartnerCampaigns() {
  const { user, isAdmin } = useAuthState();
  const [partner, setPartner] = useState<PartnerRow | null>(null);
  const [campaignRows, setCampaignRows] = useState<CampaignRow[]>([]);
  const [clicks, setClicks] = useState<ReferralClickRow[]>([]);
  const [referrals, setReferrals] = useState<ReferralInstallRow[]>([]);
  const [purchases, setPurchases] = useState<PurchaseRow[]>([]);
  const [filters, setFilters] = useState<PartnerFilterState>(DEFAULT_PARTNER_FILTERS);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [customShortCode, setCustomShortCode] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [deletingCode, setDeletingCode] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<{ code: string; message: string } | null>(null);
  const [expandedCampaignId, setExpandedCampaignId] = useState<string | null>(null);

  useSEO({
    title: 'Campaigns — LuvLust Partners',
    description: 'Create and track marketing short links for LuvLust.',
  });

  const load = useCallback(async () => {
    if (!user) return;
    setLoadError(null);
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'partners'), where('uid', '==', user.uid), limit(1)),
      );
      if (snap.empty) {
        setPartner(null);
        setCampaignRows([]);
        setClicks([]);
        setReferrals([]);
        setPurchases([]);
        return;
      }
      const docSnap = snap.docs[0];
      const p: PartnerRow = { id: docSnap.id };
      setPartner(p);

      const [campSnap, clickSnap, referralSnap, purchaseSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, 'partner_campaigns'),
            where('partnerId', '==', p.id),
            orderBy('createdAt', 'desc'),
          ),
        ),
        getDocs(
          query(collection(db, 'referral_clicks'), where('partnerId', '==', p.id)),
        ),
        getDocs(collection(db, `partners/${p.id}/referrals`)),
        getDocs(collection(db, `partners/${p.id}/purchases`)),
      ]);

      setCampaignRows(
        campSnap.docs.map((d) => {
          const c = d.data() as { name?: string; createdAt?: { seconds: number } };
          return {
            shortCode: d.id,
            name: String(c.name || 'Untitled'),
            createdAt: firestoreTimestampToDate(c.createdAt),
          };
        }),
      );
      setClicks(
        clickSnap.docs.map((d) => {
          const row = d.data();
          return {
            platform: row.platform as string | undefined,
            clickedAt: row.clickedAt,
            campaignId: row.campaignId as string | undefined,
          };
        }),
      );
      setReferrals(
        referralSnap.docs.map((d) => {
          const row = d.data();
          return {
            platform: row.platform as string | undefined,
            installedAt: row.installedAt,
            method: row.method as string | undefined,
            deviceId: row.deviceId as string | undefined,
            campaignId: row.campaignId as string | undefined,
            country: row.country as string | undefined,
          };
        }),
      );
      setPurchases(
        purchaseSnap.docs.map((d) => {
          const row = d.data();
          return {
            platform: row.platform as string | undefined,
            purchasedAt: row.purchasedAt,
            deviceId: row.deviceId as string | undefined,
            campaignId: row.campaignId as string | undefined,
          };
        }),
      );
    } catch (e) {
      console.error('Campaigns load failed:', e);
      setLoadError(e instanceof Error ? e.message : 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const campaigns = useMemo((): CampaignWithStats[] => {
    return campaignRows.map((row) => {
      const stats = computeFilteredStats(clicks, referrals, purchases, filters, {
        campaignId: row.shortCode,
      });
      return {
        ...row,
        url: `https://luvlustapp.com/r/${row.shortCode}`,
        stats,
      };
    });
  }, [campaignRows, clicks, referrals, purchases, filters]);

  const hasActiveFilters =
    filters.platform !== 'all' || Boolean(filters.dateFrom || filters.dateTo);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    const codeTrim = customShortCode.trim();
    if (!trimmed || creating || !partner) return;

    if (codeTrim && !/^[a-z0-9]{1,6}$/i.test(codeTrim)) {
      setCreateError('Short code must be 1–6 letters or numbers only.');
      return;
    }

    setCreateError(null);
    setCreating(true);
    try {
      await createPartnerCampaign({
        name: trimmed,
        ...(codeTrim ? { shortCode: codeTrim.toLowerCase() } : {}),
      });
      setName('');
      setCustomShortCode('');
      await load();
    } catch (err: unknown) {
      if (err instanceof FirebaseError) {
        switch (err.code) {
          case 'functions/already-exists':
            setCreateError('Code already taken');
            break;
          case 'functions/invalid-argument':
            setCreateError(err.message || 'Invalid campaign details');
            break;
          case 'functions/failed-precondition':
            setCreateError('Partner account not found');
            break;
          case 'functions/resource-exhausted':
            setCreateError('Could not generate a unique link. Try again or pick a custom short code.');
            break;
          case 'functions/internal':
            setCreateError('Something went wrong. Please try again.');
            break;
          default:
            setCreateError(err.message || 'Could not create campaign');
        }
      } else {
        setCreateError('Could not create campaign');
      }
    } finally {
      setCreating(false);
    }
  }

  function onShortCodeChange(value: string) {
    const lower = value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 6);
    setCustomShortCode(lower);
  }

  function copyUrl(url: string, code: string) {
    void navigator.clipboard.writeText(url);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  }

  function toggleAnalytics(shortCode: string) {
    setExpandedCampaignId((prev) => (prev === shortCode ? null : shortCode));
  }

  async function handleDelete(shortCode: string) {
    if (!window.confirm('Delete this campaign? The short link will stop working.')) {
      return;
    }
    setDeleteError(null);
    setDeletingCode(shortCode);
    try {
      await deletePartnerCampaign({ shortCode });
      await load();
    } catch (err: unknown) {
      let message = 'Could not delete campaign';
      if (err instanceof FirebaseError) {
        if (err.code === 'functions/not-found') {
          message = 'Campaign not found';
        } else if (err.code === 'functions/permission-denied') {
          message = 'Not allowed to delete this campaign';
        } else if (err.message) {
          message = err.message;
        }
      }
      setDeleteError({ code: shortCode, message });
    } finally {
      setDeletingCode(null);
    }
  }

  if (loading) {
    return <div className={styles.loading}>Loading campaigns...</div>;
  }
  if (loadError) {
    return (
      <div className={styles.loading}>
        {loadError}
        <p style={{ marginTop: 12, fontSize: 14 }}>Try refreshing the page.</p>
      </div>
    );
  }
  if (!partner) {
    return <div className={styles.loading}>Partner account not found.</div>;
  }
  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Campaigns</h1>
      <p className={styles.subtitle}>
        Create unique tracking links for your ads, emails, and posts — each link auto-routes users
        to the right app store and tracks its own stats.
      </p>

      <div className={styles.linkCard}>
        <h2 className={styles.cardTitle}>New campaign</h2>
        <form onSubmit={handleCreate} className={styles.createForm}>
          <div className={styles.createRow}>
            <input
              className={styles.nameInput}
              placeholder="Campaign name (e.g. Instagram May)"
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              maxLength={80}
              disabled={creating}
            />
            <input
              className={styles.codeInput}
              placeholder="Short code (optional)"
              value={customShortCode}
              onChange={(ev) => onShortCodeChange(ev.target.value)}
              maxLength={6}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              disabled={creating}
              aria-label="Custom short code"
            />
            <button type="submit" className={styles.createBtn} disabled={creating || !name.trim()}>
              {creating ? 'Saving…' : 'Save'}
            </button>
          </div>
          <p className={styles.fieldHint}>
            Leave short code empty for a random 6-digit link, or pick your own (1–6 letters or
            numbers, must be unique).
          </p>
        </form>
        {createError ? <p className={styles.errorText}>{createError}</p> : null}
      </div>

      <DashboardFilters filters={filters} onChange={setFilters} />

      <div className={styles.sectionHeadingRow}>
        <h2 className={styles.sectionHeading}>Active campaigns</h2>
        {hasActiveFilters ? (
          <span className={styles.sectionHeadingHint}>
            Stats reflect your selected filters
          </span>
        ) : null}
      </div>

      {campaigns.length === 0 ? (
        <div className={styles.empty}>
          No campaigns yet. Add a name above and click Save — use a custom short code or leave it
          blank for a random 6-digit link.
        </div>
      ) : (
        <div className={styles.campaignList}>
          {campaigns.map((c) => {
            const isExpanded = expandedCampaignId === c.shortCode;
            const { clicks, installs, purchases } = c.stats;
            return (
            <div
              key={c.shortCode}
              className={isExpanded ? styles.campaignRowExpanded : styles.campaignRow}
            >
              <div className={styles.campaignMain}>
                <div className={styles.campaignTitleRow}>
                  <span className={styles.campaignName}>{c.name}</span>
                  {c.createdAt ? (
                    <span className={styles.campaignDate}>
                      Created{' '}
                      {c.createdAt.toLocaleDateString(undefined, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  ) : null}
                </div>
                <code className={styles.link}>{c.url}</code>
                <div
                  className={
                    clicks === 0 && installs === 0 && purchases === 0
                      ? styles.inlineStatsMuted
                      : styles.inlineStats
                  }
                >
                  {clicks} clicks · {installs} installs
                  {clicks > 0
                    ? ` (${((installs / clicks) * 100).toFixed(0)}% of clicks)`
                    : ''}{' '}
                  · {purchases} purchases
                </div>
                {isExpanded ? <CampaignAnalyticsPanel stats={c.stats} /> : null}
                {deleteError?.code === c.shortCode ? (
                  <p className={styles.deleteErrorText}>{deleteError.message}</p>
                ) : null}
              </div>
              <div className={styles.campaignActions}>
                <button
                  type="button"
                  className={styles.analyticsBtn}
                  onClick={() => toggleAnalytics(c.shortCode)}
                  aria-expanded={isExpanded}
                >
                  <span className={isExpanded ? styles.chevronOpen : styles.chevron}>
                    ▾
                  </span>
                  Analytics
                </button>
                <button
                  type="button"
                  className={styles.copyBtn}
                  onClick={() => copyUrl(c.url, c.shortCode)}
                >
                  {copiedCode === c.shortCode ? 'Copied!' : 'Copy'}
                </button>
                {isAdmin ? (
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    disabled={deletingCode !== null}
                    onClick={() => void handleDelete(c.shortCode)}
                  >
                    {deletingCode === c.shortCode ? 'Deleting…' : 'Delete'}
                  </button>
                ) : null}
              </div>
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
}